import { FormEvent, useEffect, useState } from 'react';
import { Phone } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { AccountTenantPanel } from '@components/accounts/AccountTenantPanel';
import { PromoteAccountDialog } from '@components/accounts/PromoteAccountDialog';
import { EmptyState } from '@components/common/EmptyState';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { SectionCard } from '@components/common/SectionCard';
import { useCallProvider } from '@providers/CallProvider';
import { useAccount, useAccountTimeline, useAddAccountNote, useCallSession, useCalls, useCreateAccount, useCreateCall, useUpdateAccount, useUsers } from '@hooks/useCRM';
import { formatDate, formatRelativeDate } from '@lib/format';
import type { CRMAccountPayload, CRMCallCreatePayload } from '@/types/api';

const EMPTY_ACCOUNT_FORM: CRMAccountPayload = {
  name: '',
  status: 'lead',
  priority: 'medium',
  city: '',
  industry: '',
  website: '',
  phone: '',
  source: '',
  why_target: '',
  owner_user_id: null,
};

const ACCOUNT_STATUS_OPTIONS = [
  { id: 'lead', label: 'Lead' },
  { id: 'prospect', label: 'Prospect' },
  { id: 'customer', label: 'Customer' },
  { id: 'inactive', label: 'Inactive' },
];

const PRIORITY_OPTIONS = [
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
];

const CALL_STATUS_OPTIONS = [
  { id: 'queued', label: 'Queued' },
  { id: 'dialing', label: 'Dialing' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'completed', label: 'Completed' },
  { id: 'no_answer', label: 'No answer' },
  { id: 'busy', label: 'Busy' },
  { id: 'failed', label: 'Failed' },
];

const RECORDING_OPTIONS = [
  { id: 'yes', label: 'Requested' },
  { id: 'no', label: 'Not requested' },
];

function mapAccountToForm(account: CRMAccountPayload) {
  return {
    name: account.name ?? '',
    status: account.status ?? 'lead',
    priority: account.priority ?? 'medium',
    city: account.city ?? '',
    industry: account.industry ?? '',
    website: account.website ?? '',
    phone: account.phone ?? '',
    source: account.source ?? '',
    why_target: account.why_target ?? '',
    owner_user_id: account.owner_user_id ?? null,
  };
}

function normalizeOptional(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

type AccountDetailPageProps = {
  isCreating?: boolean;
};

export function AccountDetailPage({ isCreating = false }: AccountDetailPageProps) {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [noteBody, setNoteBody] = useState('');
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formState, setFormState] = useState<CRMAccountPayload>(EMPTY_ACCOUNT_FORM);
  const [callForm, setCallForm] = useState<CRMCallCreatePayload>({
    to_number: '',
    status: 'completed',
    recording_requested: true,
    summary: '',
    body: '',
    duration_seconds: null,
  });
  const { data: account, isLoading } = useAccount(accountId);
  const { data: timeline } = useAccountTimeline(accountId);
  const { data: callSession } = useCallSession();
  const { data: calls } = useCalls({ accountId });
  const createAccountMutation = useCreateAccount();
  const createCallMutation = useCreateCall();
  const updateAccountMutation = useUpdateAccount();
  const addNoteMutation = useAddAccountNote();
  const { data: users } = useUsers();
  const { activeCall, isStarting, startLiveCall } = useCallProvider();
  const ownerOptions = [
    { id: '', label: 'Unassigned' },
    ...(users ?? []).map((user) => ({
      id: user.id,
      label: `${user.first_name} ${user.last_name}`.trim() || user.email,
      description: user.email,
    })),
  ];

  useEffect(() => {
    if (account) {
      setSubmitError(null);
      setFormState(mapAccountToForm(account));
      setCallForm((current) => ({
        ...current,
        to_number: account.phone ?? '',
        account_id: account.id,
        owner_user_id: account.owner_user_id ?? null,
      }));
      return;
    }

    if (isCreating) {
      setSubmitError(null);
      setFormState(EMPTY_ACCOUNT_FORM);
      setCallForm({
        to_number: '',
        status: 'completed',
        recording_requested: true,
        summary: '',
        body: '',
        duration_seconds: null,
      });
    }
  }, [account, isCreating]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);

    const payload: CRMAccountPayload = {
      name: formState.name.trim(),
      status: formState.status ?? 'lead',
      priority: formState.priority ?? 'medium',
      city: normalizeOptional(formState.city ?? ''),
      industry: normalizeOptional(formState.industry ?? ''),
      website: normalizeOptional(formState.website ?? ''),
      phone: normalizeOptional(formState.phone ?? ''),
      source: normalizeOptional(formState.source ?? ''),
      why_target: normalizeOptional(formState.why_target ?? ''),
      owner_user_id: formState.owner_user_id ?? null,
    };

    try {
      if (accountId) {
        await updateAccountMutation.mutateAsync({ accountId, payload });
        return;
      }

      const createdAccount = await createAccountMutation.mutateAsync(payload);
      navigate(`/accounts/${createdAccount.id}`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to save account.');
    }
  }

  async function handleAddNote(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!accountId || !noteBody.trim()) {
      return;
    }
    await addNoteMutation.mutateAsync({
      accountId,
      payload: { summary: 'Account note', body: noteBody.trim() },
    });
    setNoteBody('');
  }

  async function handleCreateCall(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!accountId || !callForm.to_number?.trim()) {
      return;
    }
    await createCallMutation.mutateAsync({
      ...callForm,
      account_id: accountId,
      owner_user_id: account?.owner_user_id ?? null,
      to_number: callForm.to_number.trim(),
      duration_seconds: callForm.duration_seconds ? Number(callForm.duration_seconds) : null,
      summary: callForm.summary?.trim() || null,
      body: callForm.body?.trim() || null,
    });
    setCallForm((current) => ({
      ...current,
      status: 'completed',
      summary: '',
      body: '',
      duration_seconds: null,
    }));
  }

  async function handleStartLiveCall() {
    if (!accountId || !callForm.to_number?.trim()) {
      return;
    }
    await startLiveCall({
      to_number: callForm.to_number.trim(),
      account_id: accountId,
      owner_user_id: account?.owner_user_id ?? null,
      recording_requested: callForm.recording_requested ?? true,
      consent_status: callForm.consent_status ?? 'unknown',
    });
  }

  const isSaving = createAccountMutation.isPending || updateAccountMutation.isPending;

  if (!isCreating && !accountId) {
    return <EmptyState title="Account not found" body="Select an account from the list to view its profile." />;
  }

  return (
    <div className="detail-stack">
      <section className="entity-header">
        <div className="entity-header-copy">
          <p className="entity-breadcrumb">Accounts / {accountId ? 'Detail' : 'New'}</p>
          <h1>{account?.name || (accountId ? 'Account Detail' : 'Create Account')}</h1>
          <p>
            {account
              ? `${account.industry || 'Unspecified industry'} in ${account.city || 'unknown location'} with ${account.status} status.`
              : 'Create a dedicated account record, then manage calls, notes, and account history on this page.'}
          </p>
        </div>
        <div className="entity-header-actions">
          <button type="button" className="button button-secondary" onClick={() => navigate('/accounts')}>Back to Accounts</button>
        </div>
      </section>

      {account ? (
        <section className="entity-meta-grid">
          <article className="entity-meta-card">
            <span>Status</span>
            <strong>{account.status}</strong>
          </article>
          <article className="entity-meta-card">
            <span>Priority</span>
            <strong>{account.priority}</strong>
          </article>
          <article className="entity-meta-card">
            <span>Owner</span>
            <strong>{users?.find((user) => user.id === account.owner_user_id)?.first_name || 'Unassigned'}</strong>
          </article>
          <article className="entity-meta-card">
            <span>Last Activity</span>
            <strong>{formatRelativeDate(account.last_activity_at)}</strong>
          </article>
        </section>
      ) : null}

      <SectionCard
        title={accountId ? 'Account Detail' : 'New Account'}
        subtitle="Update account context, founder notes, and customer-fit signals."
      >
        {isLoading ? (
          <div className="loading-row">Loading account detail…</div>
        ) : !isCreating && !account ? (
          <EmptyState title="Account not found" body="This account record could not be loaded." />
        ) : (
          <form className="detail-form" onSubmit={handleSubmit}>
            <div className="form-grid">
              <label className="form-field">
                <span>Name</span>
                <input
                  value={formState.name}
                  onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
                  placeholder="Acme Corp"
                  required
                />
              </label>
              <label className="form-field">
                <span>Status</span>
                <SearchableSelect
                  options={ACCOUNT_STATUS_OPTIONS}
                  value={formState.status ?? 'lead'}
                  onChange={(value) => setFormState((current) => ({ ...current, status: value }))}
                  placeholder="Select status"
                  clearable={false}
                />
              </label>
              <label className="form-field">
                <span>Priority</span>
                <SearchableSelect
                  options={PRIORITY_OPTIONS}
                  value={formState.priority ?? 'medium'}
                  onChange={(value) => setFormState((current) => ({ ...current, priority: value }))}
                  placeholder="Select priority"
                  clearable={false}
                />
              </label>
              <label className="form-field">
                <span>City</span>
                <input
                  value={formState.city ?? ''}
                  onChange={(event) => setFormState((current) => ({ ...current, city: event.target.value }))}
                  placeholder="Stockholm"
                />
              </label>
              <label className="form-field">
                <span>Industry</span>
                <input
                  value={formState.industry ?? ''}
                  onChange={(event) => setFormState((current) => ({ ...current, industry: event.target.value }))}
                  placeholder="Accounting software"
                />
              </label>
              <label className="form-field">
                <span>Website</span>
                <input
                  value={formState.website ?? ''}
                  onChange={(event) => setFormState((current) => ({ ...current, website: event.target.value }))}
                  placeholder="https://acme.com"
                />
              </label>
              <label className="form-field">
                <span>Phone</span>
                <input
                  value={formState.phone ?? ''}
                  onChange={(event) => setFormState((current) => ({ ...current, phone: event.target.value }))}
                  placeholder="+1 555 010 100"
                />
              </label>
              <label className="form-field">
                <span>Source</span>
                <input
                  value={formState.source ?? ''}
                  onChange={(event) => setFormState((current) => ({ ...current, source: event.target.value }))}
                  placeholder="Outbound, referral, demo request"
                />
              </label>
              <label className="form-field">
                <span>Owner</span>
                <SearchableSelect
                  options={ownerOptions}
                  value={formState.owner_user_id ?? ''}
                  onChange={(value) => setFormState((current) => ({ ...current, owner_user_id: value || null }))}
                  placeholder="Unassigned"
                  clearable={false}
                />
              </label>
            </div>

            <label className="form-field">
              <span>Why target</span>
              <textarea
                rows={4}
                value={formState.why_target ?? ''}
                onChange={(event) => setFormState((current) => ({ ...current, why_target: event.target.value }))}
                placeholder="Why this account matters right now."
              />
            </label>

            <div className="form-actions">
              <button type="submit" className="button button-primary" disabled={isSaving}>
                {isSaving ? 'Saving…' : accountId ? 'Save Account' : 'Create Account'}
              </button>
            </div>
            {submitError ? <p className="form-error">{submitError}</p> : null}
          </form>
        )}
      </SectionCard>

      {accountId && account ? (
        <>
          <SectionCard
            title="Customer Tenants"
            subtitle="Create and manage the product tenants owned by this CRM account."
          >
            <AccountTenantPanel accountId={account.id} onCreateTenant={() => setPromoteOpen(true)} />
          </SectionCard>

          <PromoteAccountDialog
            accountId={account.id}
            accountName={account.name}
            open={promoteOpen}
            onClose={() => setPromoteOpen(false)}
            mode={account.linked_tenant_id ? 'create_tenant' : 'promote'}
          />

          <SectionCard title="Calls" subtitle="Call records for this account and live dialer entry point.">
            <div className="detail-stack">
              <div className="call-session-banner">
                <div>
                  <strong>{callSession?.available ? 'Dialer config ready' : 'Dialer setup incomplete'}</strong>
                  <p>
                    {callSession?.available
                      ? `LiveKit session metadata is available for caller ID ${callSession.outbound_caller_id ?? 'not set'}.`
                      : `Missing: ${callSession?.missing_configuration.join(', ') || 'LIVEKIT_WS_URL, LIVEKIT_API_KEY, LIVEKIT_API_SECRET, CRM_OUTBOUND_CALLER_ID'}`}
                  </p>
                </div>
                <span className={`pill ${callSession?.available ? 'pill-medium' : 'pill-low'}`}>
                  {callSession?.provider ?? 'livekit'}
                </span>
              </div>
              <form className="detail-subsection call-log-panel" onSubmit={handleCreateCall}>
                <div className="form-grid">
                  <label className="form-field">
                    <span>To Number</span>
                    <input
                      value={callForm.to_number ?? ''}
                      onChange={(event) => setCallForm((current) => ({ ...current, to_number: event.target.value }))}
                      placeholder="+1 555 010 100"
                    />
                  </label>
                  <label className="form-field">
                    <span>Status</span>
                    <SearchableSelect
                      options={CALL_STATUS_OPTIONS}
                      value={callForm.status ?? 'completed'}
                      onChange={(value) => setCallForm((current) => ({ ...current, status: value }))}
                      placeholder="Select status"
                      clearable={false}
                    />
                  </label>
                  <label className="form-field">
                    <span>Duration Seconds</span>
                    <input
                      type="number"
                      min="0"
                      value={callForm.duration_seconds ?? ''}
                      onChange={(event) => setCallForm((current) => ({ ...current, duration_seconds: event.target.value ? Number(event.target.value) : null }))}
                      placeholder="300"
                    />
                  </label>
                  <label className="form-field">
                    <span>Recording</span>
                    <SearchableSelect
                      options={RECORDING_OPTIONS}
                      value={callForm.recording_requested ? 'yes' : 'no'}
                      onChange={(value) => setCallForm((current) => ({ ...current, recording_requested: value === 'yes' }))}
                      placeholder="Recording"
                      clearable={false}
                    />
                  </label>
                </div>
                <label className="form-field">
                  <span>Summary</span>
                  <input
                    value={callForm.summary ?? ''}
                    onChange={(event) => setCallForm((current) => ({ ...current, summary: event.target.value }))}
                    placeholder="Confirmed accounting pain points and agreed to a follow-up."
                  />
                </label>
                <label className="form-field">
                  <span>Notes</span>
                  <textarea
                    rows={3}
                    value={callForm.body ?? ''}
                    onChange={(event) => setCallForm((current) => ({ ...current, body: event.target.value }))}
                    placeholder="Capture what happened on the call."
                  />
                </label>
                <div className="form-actions">
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={() => void handleStartLiveCall()}
                    disabled={!callSession?.available || !callForm.to_number?.trim() || isStarting || Boolean(activeCall)}
                  >
                    <Phone size={16} />
                    {isStarting ? 'Connecting…' : activeCall ? 'Live Call Active' : 'Start Live Call'}
                  </button>
                  <button type="submit" className="button button-secondary" disabled={createCallMutation.isPending || !callForm.to_number?.trim()}>
                    <Phone size={16} />
                    {createCallMutation.isPending ? 'Saving Call…' : 'Save Call Record'}
                  </button>
                </div>
              </form>
              {calls?.items.length ? (
                <div className="timeline-list">
                  {calls.items.map((call) => (
                    <article key={call.id} className="timeline-item call-item">
                      <div className="timeline-meta">
                        <strong>{call.to_number}</strong>
                        <span>{formatRelativeDate(call.ended_at || call.created_at)}</span>
                      </div>
                      <div className="call-item-meta">
                        <span className="pill pill-neutral">{call.status.replace('_', ' ')}</span>
                        <span className="pill pill-soft">{call.recording_status}</span>
                        <span className="pill pill-soft">{call.transcript_status}</span>
                      </div>
                      <p>{call.duration_seconds ? `${call.duration_seconds}s` : 'No duration captured yet'}</p>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="muted-copy">No calls logged for this account yet.</p>
              )}
            </div>
          </SectionCard>

          <SectionCard title="Account Timeline" subtitle="Latest activity for this account.">
            {timeline?.items.length ? (
              <div className="timeline-list">
                {timeline.items.map((activity) => (
                  <article key={activity.id} className="timeline-item">
                    <div className="timeline-meta">
                      <strong>{activity.summary}</strong>
                      <span>{formatDate(activity.occurred_at)}</span>
                    </div>
                    <p>{activity.body || 'No additional note body.'}</p>
                  </article>
                ))}
              </div>
            ) : (
              <p className="muted-copy">No account activity yet.</p>
            )}
          </SectionCard>

          <SectionCard title="Add Note" subtitle="Capture research, context, or customer signals.">
            <form className="detail-subsection" onSubmit={handleAddNote}>
              <label className="form-field">
                <span>Note</span>
                <textarea
                  rows={3}
                  value={noteBody}
                  onChange={(event) => setNoteBody(event.target.value)}
                  placeholder="What changed, what matters, and what to do next."
                />
              </label>
              <div className="form-actions">
                <button type="submit" className="button button-secondary" disabled={addNoteMutation.isPending || !noteBody.trim()}>
                  {addNoteMutation.isPending ? 'Saving Note…' : 'Save Note'}
                </button>
              </div>
            </form>
          </SectionCard>
        </>
      ) : null}
    </div>
  );
}
